import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import SectionBanner from '../components/SectionBanner';
import {
  myBusinesses, createBusiness, approveBeneficiaries, createBatch,
  requestApproval, approveBatch, listBatches, getBatch, downloadTemplate, parseCsv, fmtMonto,
} from '../lib/business';

const STATUS_STYLE = {
  BORRADOR: 'bg-[#F2F4F7] text-[#475467]',
  PENDIENTE_APROBACION: 'bg-[#FFFAEB] text-[#B54708]',
  APROBADO: 'bg-[#EFF8FF] text-[#175CD3]',
  EN_DISPERSION: 'bg-[#EFF8FF] text-[#175CD3]',
  COMPLETADO: 'bg-[#ECFDF3] text-[#047857]',
  PARCIAL: 'bg-[#FFFAEB] text-[#B54708]',
  RECHAZADO: 'bg-[#FEF3F2] text-[#B91C1C]',
  EXPIRADO: 'bg-[#F2F4F7] text-[#475467]',
  pagado: 'text-[#047857]', simulado: 'text-[#175CD3]', fallido: 'text-[#B91C1C]', pendiente: 'text-[#6B7280]',
};
const Chip = ({ s }) => (
  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[s] || 'bg-[#F2F4F7] text-[#475467]'}`}>{s?.replace(/_/g, ' ')}</span>
);

export default function Business() {
  const snap = useSnapshot(state);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  // crear empresa
  const [name, setName] = useState('');
  const [nit, setNit] = useState('');
  // lote
  const [rows, setRows] = useState(null);
  const [csvErrors, setCsvErrors] = useState([]);
  const [batch, setBatch] = useState(null);
  const [newBens, setNewBens] = useState([]);
  const [actionToken, setActionToken] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);
  const fileRef = useRef(null);

  const phone = snap.user?.phonenumber?.number
    ? `${snap.user.phonenumber.nationality?.area_code || ''}${snap.user.phonenumber.number}`
    : '';

  const reload = async (biz) => {
    const b = biz || business;
    if (b) listBatches(b._id).then(setBatches).catch(() => {});
  };

  useEffect(() => {
    myBusinesses()
      .then((list) => { setBusiness(list[0] || null); if (list[0]) reload(list[0]); })
      .catch((e) => setErr(e?.response?.data?.message || 'No se pudo cargar'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const fail = (e, fallback) => { setMsg(null); setErr(e?.response?.data?.message || fallback); };

  const onCreateBusiness = async () => {
    setBusy(true); setErr(null);
    try { const b = await createBusiness(name.trim(), nit.trim()); setBusiness(b); setMsg('Empresa creada. Ahora sube tu primera nomina.'); }
    catch (e) { fail(e, 'No se pudo crear la empresa'); }
    finally { setBusy(false); }
  };

  const onFile = (f) => {
    const reader = new FileReader();
    reader.onload = () => { const { rows: r, errors } = parseCsv(reader.result); setRows(r); setCsvErrors(errors); setBatch(null); setNewBens([]); setActionToken(null); };
    reader.readAsText(f);
  };

  const onCreateBatch = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await createBatch(business._id, `Nomina ${new Date().toLocaleDateString('es-CO')}`, rows);
      setBatch(res.data); setNewBens(res.newBeneficiaries || []); setRows(null); setMsg(null); reload();
    } catch (e) {
      const v = e?.response?.data?.validationErrors;
      fail(e, 'El lote tiene errores'); if (v?.length) setCsvErrors(v);
    } finally { setBusy(false); }
  };

  const onApproveBens = async () => {
    setBusy(true); setErr(null);
    try {
      const toApprove = batch.rows.filter((r) => newBens.some((n) => n.docId === r.docId))
        .map((r) => ({ docId: r.docId, name: r.name, phone: r.phone, destType: r.destType, destRef: r.destRef }));
      await approveBeneficiaries(business._id, toApprove);
      // recrear el lote para que las filas queden sin la marca de beneficiario nuevo
      const res = await createBatch(business._id, batch.label, batch.rows);
      setBatch(res.data); setNewBens(res.newBeneficiaries || []); setMsg('Beneficiarios aprobados.'); reload();
    } catch (e) { fail(e, 'No se pudieron aprobar los beneficiarios'); }
    finally { setBusy(false); }
  };

  const onRequestApproval = async () => {
    setBusy(true); setErr(null);
    try { const res = await requestApproval(business._id, batch._id, phone); setActionToken(res.actionToken); setMsg('Codigo enviado a tu celular.'); }
    catch (e) { fail(e, 'No se pudo enviar el codigo'); }
    finally { setBusy(false); }
  };

  const onApprove = async () => {
    setBusy(true); setErr(null);
    try { const b = await approveBatch(business._id, batch._id, actionToken, code.trim()); setBatch(b); setActionToken(null); setCode(''); setMsg('Nomina aprobada y dispersada.'); reload(); }
    catch (e) { fail(e, 'Codigo incorrecto o expirado'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-center py-10 text-[#9CA3AF]">Cargando...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <SectionBanner
        id="business"
        title="FlowexPay Business — Nomina con control"
        text="Tu equipo prepara la nomina; tu la apruebas desde el celular. Nadie mueve dinero sin ti."
      />

      {err && <div className="bg-[#FEF3F2] text-[#B91C1C] text-sm rounded-lg px-4 py-3 mb-4">{err}</div>}
      {msg && <div className="bg-[#ECFDF3] text-[#047857] text-sm rounded-lg px-4 py-3 mb-4">{msg}</div>}

      {!business ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-6">
          <h2 className="text-lg font-bold mb-1">Vincula tu empresa</h2>
          <p className="text-sm text-[#6B7280] mb-4">Tu quedas como dueno: el unico que aprueba pagos. (Modo piloto — a clientes reales los vincula FlowexPay con verificacion previa.)</p>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre de la empresa" className="w-full border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm mb-3" />
          <input value={nit} onChange={(e) => setNit(e.target.value)} placeholder="NIT" className="w-full border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm mb-4" />
          <button disabled={busy || !name.trim() || !nit.trim()} onClick={onCreateBusiness} className="px-5 py-2.5 rounded-lg bg-[#0a2540] text-white text-sm font-semibold disabled:opacity-50">Crear empresa</button>
        </div>
      ) : (
        <>
          {/* Paso 1: subir nomina */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 mb-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-bold">{business.name}</h2>
              <button onClick={downloadTemplate} className="text-xs font-semibold text-[#2A59C6]">Descargar plantilla</button>
            </div>
            <p className="text-xs text-[#6B7280] mb-4">Sube la nomina en la plantilla (CSV). El sistema valida cada pago antes de crear el lote.</p>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
            <button onClick={() => fileRef.current?.click()} className="px-4 py-2.5 rounded-lg border border-[#2A59C6] text-[#2A59C6] text-sm font-semibold">Subir nomina (CSV)</button>

            {csvErrors.length > 0 && (
              <div className="mt-3 bg-[#FEF3F2] rounded-lg px-4 py-3 text-xs text-[#B91C1C]">
                {csvErrors.map((e2, i) => <div key={i}>{e2}</div>)}
              </div>
            )}

            {rows && rows.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-semibold mb-2">{rows.length} pagos leidos</div>
                <div className="border border-[#EFF1F4] rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                  {rows.map((r, i) => (
                    <div key={i} className="flex justify-between px-4 py-2 border-b border-[#F5F5F5] text-xs">
                      <span>{r.name} <span className="text-[#9CA3AF]">· {r.destType}</span></span>
                      <span className="font-semibold">{fmtMonto(r)}</span>
                    </div>
                  ))}
                </div>
                <button disabled={busy} onClick={onCreateBatch} className="mt-3 px-5 py-2.5 rounded-lg bg-[#0a2540] text-white text-sm font-semibold disabled:opacity-50">Crear lote de nomina</button>
              </div>
            )}
          </div>

          {/* Paso 2: lote actual + aprobacion */}
          {batch && (
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 mb-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold">{batch.label}</h3>
                <Chip s={batch.status} />
              </div>
              <div className="text-xs text-[#6B7280] mb-3">
                {batch.totals?.countRows} pagos · COP ${Math.round(batch.totals?.totalCop || 0).toLocaleString('es-CO')} · USD ${Number(batch.totals?.totalUsd || 0).toLocaleString('en-US')}
              </div>
              <div className="border border-[#EFF1F4] rounded-lg overflow-hidden max-h-56 overflow-y-auto mb-4">
                {(batch.rows || []).map((r, i) => (
                  <div key={i} className="flex justify-between px-4 py-2 border-b border-[#F5F5F5] text-xs">
                    <span>{r.name}{r.newBeneficiary ? <span className="ml-2 text-[#B54708] font-semibold">nuevo</span> : null}{r.error ? <span className="ml-2 text-[#B91C1C]">{r.error}</span> : null}</span>
                    <span className={`font-semibold ${STATUS_STYLE[r.status] || ''}`}>{fmtMonto(r)} · {r.status}</span>
                  </div>
                ))}
              </div>

              {newBens.length > 0 && (
                <div className="bg-[#FFFAEB] rounded-lg px-4 py-3 mb-3">
                  <div className="text-xs text-[#B54708] font-semibold mb-2">{newBens.length} beneficiario(s) nuevo(s): requieren tu aprobacion antes de pagar (proteccion anti-fraude).</div>
                  <button disabled={busy} onClick={onApproveBens} className="px-4 py-2 rounded-lg bg-[#B54708] text-white text-xs font-semibold disabled:opacity-50">Aprobar beneficiarios (dueno)</button>
                </div>
              )}

              {['BORRADOR', 'PENDIENTE_APROBACION', 'EXPIRADO'].includes(batch.status) && newBens.length === 0 && (
                !actionToken ? (
                  <button disabled={busy || !phone} onClick={onRequestApproval} className="px-5 py-2.5 rounded-lg bg-[#047857] text-white text-sm font-semibold disabled:opacity-50">
                    Aprobar nomina (enviar codigo SMS)
                  </button>
                ) : (
                  <div className="flex gap-2 items-center">
                    <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Codigo SMS" maxLength={8} className="border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm w-36" />
                    <button disabled={busy || code.trim().length < 4} onClick={onApprove} className="px-5 py-2.5 rounded-lg bg-[#047857] text-white text-sm font-semibold disabled:opacity-50">Confirmar y dispersar</button>
                  </div>
                )
              )}
            </div>
          )}

          {/* Historial */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <h3 className="text-sm font-bold mb-3">Lotes de nomina</h3>
            {batches.length === 0 ? (
              <div className="text-xs text-[#9CA3AF]">Aun no hay lotes. Sube tu primera nomina.</div>
            ) : (
              batches.map((b) => (
                <button key={b._id} onClick={() => getBatch(business._id, b._id).then((full) => { setBatch(full); setNewBens((full.rows || []).filter((r) => r.newBeneficiary).map((r) => ({ docId: r.docId, name: r.name }))); })}
                  className="w-full flex justify-between items-center px-3 py-2.5 border-b border-[#F5F5F5] last:border-b-0 text-left hover:bg-[#FAFAFA]">
                  <span className="text-xs">{b.label} <span className="text-[#9CA3AF]">· {new Date(b.createdAt).toLocaleDateString('es-CO')}</span></span>
                  <Chip s={b.status} />
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
